import * as React from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input, type InputProps } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Campo de formulário "tudo-em-um" que junta label, input e mensagem de erro,
 * integrado ao react-hook-form via Controller. Reduz boilerplate nas páginas de
 * auth/cadastro.
 */
interface FormFieldProps<T extends FieldValues>
  extends Omit<InputProps, 'name' | 'form' | 'defaultValue'> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  hint?: string;
  inputClassName?: string;
}

export function FormField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  className,
  inputClassName,
  ...inputProps
}: FormFieldProps<T>) {
  const id = React.useId();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const errorId = fieldState.error ? `${id}-error` : undefined;
        return (
          <div className={cn('space-y-1.5', className)}>
            <Label htmlFor={id}>{label}</Label>
            <Input
              id={id}
              aria-invalid={!!fieldState.error}
              aria-describedby={errorId}
              className={inputClassName}
              {...field}
              value={field.value ?? ''}
              {...inputProps}
            />
            {fieldState.error ? (
              <p id={errorId} className="text-xs font-medium text-destructive">
                {fieldState.error.message}
              </p>
            ) : hint ? (
              <p className="text-xs text-muted-foreground">{hint}</p>
            ) : null}
          </div>
        );
      }}
    />
  );
}
